/**
 * Walkthrough Utilities
 * Helper functions for the interactive walkthrough feature
 */

/**
 * Get the position and dimensions of an element with optional padding
 * @param {string} selector - CSS selector for the target element
 * @param {number} padding - Padding around the element (default: 8px)
 * @returns {Object|null} Element bounds or null if not found
 */
export function getElementPosition(selector, padding = 8) {
  const element = document.querySelector(selector);

  if (!element) {
    console.warn(`Walkthrough: Element not found for selector "${selector}"`);
    return null;
  }

  const rect = element.getBoundingClientRect();

  return {
    top: rect.top - padding,
    left: rect.left - padding,
    width: rect.width + (padding * 2),
    height: rect.height + (padding * 2),
    bottom: rect.bottom + padding,
    right: rect.right + padding,
    element
  };
}

/**
 * Calculate optimal tooltip position to keep it within viewport
 * @param {Object} elementRect - Bounding rect of the highlighted element
 * @param {string} placement - Preferred placement ('top', 'right', 'bottom', 'left')
 * @returns {Object} Position styles for the tooltip
 */
export function getOptimalTooltipPosition(elementRect, placement = 'right') {
  const tooltipPadding = 16; // Space between tooltip and highlighted element
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const tooltipWidth = 340; // Estimated tooltip width (increased for safety)
  const tooltipHeight = 250; // Estimated tooltip height (increased for safety)
  const edgePadding = 20; // Minimum distance from viewport edges

  let position = {
    top: 'auto',
    left: 'auto',
    right: 'auto',
    bottom: 'auto'
  };

  // Calculate position based on placement preference
  switch (placement) {
    case 'right':
      position.left = elementRect.right + tooltipPadding;
      position.top = elementRect.top;

      // If it would overflow right, try left
      if (position.left + tooltipWidth > viewportWidth - 20) {
        position.left = 'auto';
        position.right = viewportWidth - elementRect.left + tooltipPadding;
      }
      break;

    case 'left':
      position.right = viewportWidth - elementRect.left + tooltipPadding;
      position.top = elementRect.top;

      // If it would overflow left, try right
      if (viewportWidth - position.right < tooltipWidth) {
        position.right = 'auto';
        position.left = elementRect.right + tooltipPadding;
      }
      break;

    case 'bottom':
      position.top = elementRect.bottom + tooltipPadding;

      // Center horizontally if element is wider than tooltip
      if (elementRect.width > tooltipWidth) {
        position.left = elementRect.left + (elementRect.width - tooltipWidth) / 2;
      } else {
        position.left = elementRect.left;
      }

      // If it would overflow bottom, try top instead
      if (position.top + tooltipHeight > viewportHeight - edgePadding) {
        position.top = 'auto';
        const bottomPos = viewportHeight - elementRect.top + tooltipPadding;
        // Make sure there's enough room at top
        if (viewportHeight - bottomPos - tooltipHeight > edgePadding) {
          position.bottom = bottomPos;
        } else {
          // Not enough room top or bottom, position at viewport center
          position.top = (viewportHeight - tooltipHeight) / 2;
          position.bottom = 'auto';
        }
      }
      break;

    case 'top':
      position.bottom = viewportHeight - elementRect.top + tooltipPadding;
      position.left = elementRect.left;

      // If it would overflow top, try bottom
      if (viewportHeight - position.bottom < tooltipHeight) {
        position.bottom = 'auto';
        position.top = elementRect.bottom + tooltipPadding;
      }

      // Center horizontally if possible
      if (elementRect.width > tooltipWidth) {
        position.left = elementRect.left + (elementRect.width - tooltipWidth) / 2;
      }
      break;

    default:
      position.left = elementRect.right + tooltipPadding;
      position.top = elementRect.top;
  }

  // Ensure tooltip stays within viewport bounds
  if (position.left !== 'auto') {
    if (position.left + tooltipWidth > viewportWidth - edgePadding) {
      position.left = viewportWidth - tooltipWidth - edgePadding;
    }
    if (position.left < edgePadding) {
      position.left = edgePadding;
    }
  }

  if (position.top !== 'auto') {
    if (position.top + tooltipHeight > viewportHeight - edgePadding) {
      position.top = viewportHeight - tooltipHeight - edgePadding;
    }
    if (position.top < edgePadding) {
      position.top = edgePadding;
    }
  }

  return position;
}

/**
 * Smooth scroll to bring an element into view
 * @param {HTMLElement} element - The element to scroll to
 * @param {number} offset - Additional offset from top (default: 100px for fixed header)
 */
export function scrollToElement(element, offset = 100) {
  if (!element) return;

  const elementRect = element.getBoundingClientRect();
  const absoluteElementTop = elementRect.top + window.pageYOffset;
  const middle = absoluteElementTop - offset;

  window.scrollTo({
    top: middle,
    behavior: 'smooth'
  });
}

/**
 * Check if an element is in the viewport
 * @param {HTMLElement} element - The element to check
 * @returns {boolean} True if element is in viewport
 */
export function isElementInViewport(element) {
  if (!element) return false;

  const rect = element.getBoundingClientRect();

  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
}
